import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const ChipmanAB = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e9%98%bf%e5%b0%94%e4%bc%af%e5%a1%94%e7%9c%81-%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/alberta-neighbourhoods/'

  const negotiateRentLink = isChinese
    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e4%b8%8e%e6%88%bf%e4%b8%9c%e5%95%86%e8%ae%a8%e7%a7%9f%e9%87%91%e7%9a%846%e4%b8%aa%e6%8a%80%e5%b7%a7/'
    : 'https://liv.rent/blog/renters/how-to-negotiate-rent/'

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.chipman-ab:About Chipman Paragraph 1">
        <Paragraph>
          Chipman, <LinkText href="/rental-listings/alberta">Alberta</LinkText>,
          is a quaint village that epitomizes rural Alberta’s quiet charm and
          community spirit. Though small in size, Chipman is big in heart,
          offering residents a peaceful, close-knit environment that is often
          sought by individuals eager to escape the hustle and bustle of urban
          life. Its historical roots in agriculture continue to shape its
          character and economy, reflected in events like the annual Chipman Car
          Crafters Show, which draws enthusiasts for a celebration of vintage
          automobiles and local culture. The surrounding natural beauty offers
          everyday solace and outdoor recreational opportunities, while the
          village’s strategic location, a short drive east of{' '}
          <LinkText href="/rental-listings/city/edmonton">Edmonton</LinkText>,
          ensures easy access to urban amenities when needed. Chipman represents
          a blend of the tranquil lifestyle, community support, and
          accessibility that are hallmarks of living in rural Alberta.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $2,000',
          bed2: '$1,700 - $2,700',
          bed3: '$2,300 - $3,600'
        }}
        rentReportCity={t('states:Alberta')}
        rentReportLink="https://liv.rent/blog/rent-reports/alberta/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.chipman-ab:Apartment Hunting in Chipman Paragraph 1">
            Finding affordable rentals in Chipman is easier compared to other
            major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and <LinkText href="/rental-listing/city/toronto">Toronto</LinkText>
            , especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href={negotiateRentLink}>how to negotiate rent</LinkText>.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.chipman-ab:Apartment Hunting in Chipman Paragraph 2">
            Chipman offers a diverse range of neighborhoods and municipalities
            to choose from when searching for rentals. Depending on your
            preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and
            <LinkText href={links.studio}>studio apartments</LinkText>. Whether
            you’re seeking{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> or{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>,
            or even considering
            <LinkText href={links.longTerm}>long term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>,
            Chipman has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Apartments for rent Chipman Paragraph">
              <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
              are not common in Chipman. Typically, they are more suitable for
              singles, couples, or small families looking for modern amenities
              and less maintenance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Houses for rent Chipman Paragraph">
              Detached houses for rent are a popular form of housing in Chipman.
              They are suitable for families seeking more space or yard. Both
              partial houses and entire{' '}
              <LinkText href={links.house}>houses for rent</LinkText> are
              common, with a mix of older and newer homes depending on which
              part of the city you’re looking in.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Townhouses for rent Chipman Paragraph">
              Townhouses are also common in Chipman. They combine the best
              qualities of both apartments and whole houses. Typically less
              expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in Chipman
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Basements for rent Alberta Paragraph">
              A popular alternative for students,{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent in {{ cityName }} as a cheaper
              alternative. Homeowners often rent out their basements as a way to
              offset their mortgage payments. This makes it easy for renters to
              find relatively inexpensive basements for rent in all areas of the
              city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Condos for rent Alberta Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Chipman
              </LinkText>{' '}
              are not a common form of rental housing. These types of rental
              accommodation are perfect for those who like to be in the centre
              of the action, and don’t need too much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Short term rentals Chipman Paragraph">
              Chipman, a small village in Alberta, does not carry the
              distinction of being Canada’s largest city and isn’t characterized
              by a wide range of job opportunities or an extensive educational
              sector. Instead, it offers a closer-knit community lifestyle,
              different from the nation’s metropolitan areas.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
              aren’t as prevalent in Chipman as they are in larger cities,
              particularly because it doesn’t experience the typical influx of
              students or seasonal workers. However, for those seeking a
              quieter, more rural experience or temporary lodging during work in
              the region, the available rental options may suffice. The appeal
              of short-term rentals is the flexibility, which is a key factor
              when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.chipman-ab:Cheap rentals Chipman Paragraph">
              Compared to other Canadian cities, rent prices in Chipman are
              relatively cheaper. As a result, it shouldn’t be too hard to find
              something within your budget. If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Chipman, AB
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in Chipman where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.chipman-ab:Living in Chipman Paragraph">
          Chipman, <LinkText href="/rental-listings/alberta">Alberta</LinkText>,
          offers a distinct experience, ideal for those who cherish a serene
          village lifestyle away from the bustle of urban centers. Known for its
          tight-knit community, Chipman is primarily catered to individuals,
          couples, and families seeking peace or a slower pace of life. While it
          may not boast the extensive recreational activities found in larger
          cities, it compensates with a strong sense of community, local events,
          and easy access to outdoor activities, emphasizing the natural beauty
          of the Alberta countryside. The area is suited for nature enthusiasts,
          retirees, or those who work in nearby areas and prefer a quiet, rural
          residence. Chipman is also recognized for its annual events like the
          Chipman Car Crafters Show, which reflect the local culture and
          community spirit, drawing both residents and visitors for unique,
          communal celebrations.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.chipman-ab:Popular Neighbourhoods Chipman Paragraph">
          Like any city, Chipman is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Central Chipman, North Chipman, South Chipman, Chipman Trailer Park,
          Riverside, and Chipman Campground. For more info about Chipman, you
          can consult our blog on{' '}
          <LinkText href={neighborhoodsLink}>Alberta neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.chipman-ab:Local Landmarks Chipman Paragraph">
          In the small village of Chipman, Alberta, several landmarks capture
          the essence of its history and community spirit. The Chipman National
          Historic Site, marked by a distinctive grain elevator, stands as a
          symbol of the region’s agricultural roots and contributes
          significantly to the village’s skyline. Nearby, the Chipman United
          Church, with its classic architecture, offers a glimpse into the
          community’s longstanding spiritual and cultural values. The Chipman
          Car Crafters Show is more of an event than a place, yet it transforms
          the locale into a landmark of sorts annually, celebrating vintage
          automobiles and attracting enthusiasts from various regions. Finally,
          for nature lovers, the vast expanses of pristine, rural landscapes
          surrounding Chipman serve as a living landmark, inviting residents and
          visitors alike to explore the natural beauty inherent to this part of
          Alberta.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default ChipmanAB
